import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { invokeEdgeFunction } from '@/lib/edgeFunctionRetry';
import { isPreviewHost, paymentDisabledMessage } from '@/lib/payments/paymentMode';
import { openCheckoutUrl } from '@/lib/payments/openCheckout';

export type FeatureKey =
  | 'mentor_message'
  | 'card_generation'
  | 'card_image_regen'
  | 'career_insights'
  | 'relationship_report'
  | 'framework_correlation'
  | 'collection_generation'
  | 'unified_insights_refresh';

interface FeatureLimit {
  free: number;
  pro: number;
  period: 'daily' | 'weekly';
}

export const FEATURE_LIMITS: Record<FeatureKey, FeatureLimit> = {
  mentor_message:           { free: 10,  pro: 200, period: 'daily' },
  card_generation:          { free: 1,   pro: 100, period: 'daily' },
  card_image_regen:         { free: 0,   pro: 100, period: 'daily' },
  career_insights:          { free: 0,   pro: 100, period: 'daily' },
  relationship_report:      { free: 0,   pro: 100, period: 'daily' },
  framework_correlation:    { free: 1,   pro: 100, period: 'daily' },
  collection_generation:    { free: 2,   pro: 20,  period: 'daily' },
  unified_insights_refresh: { free: 1,   pro: 100, period: 'weekly' },
};

export type PlanType = 'free' | 'pro';

interface SubscriptionData {
  plan_type: string;
  status: string;
  current_period_end: string | null;
  stripe_customer_id: string | null;
  /**
   * Whether Stripe is configured with a live key. `null` = unknown (the check
   * failed and we fell back to the DB); unknown is treated as live so a
   * transient edge-function error never blocks a genuine upgrade.
   */
  payments_live?: boolean | null;
}

export function useSubscription() {
  const { user } = useAuth();

  // Check if user is admin
  const { data: isAdmin, isLoading: isLoadingAdmin } = useQuery({
    queryKey: ['is-admin', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const { data: subscription, isLoading: isLoadingSubscription } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async (): Promise<SubscriptionData | null> => {
      if (!user?.id) return null;
      try {
        const data = await invokeEdgeFunction<{ plan_type?: string; subscription_end?: string | null; payments_live?: boolean; auth_unavailable?: boolean }>('check-subscription', {
          timeout: 15_000,
        });
        // Auth service was momentarily unreachable — fall through to the DB
        // read rather than downgrading the user to free.
        if (data?.auth_unavailable) throw new Error('auth_unavailable');
        return {
          plan_type: data?.plan_type || 'free',
          status: 'active',
          current_period_end: data?.subscription_end || null,
          stripe_customer_id: null,
          payments_live: data?.payments_live ?? null,
        };
      } catch (error) {
        console.error('check-subscription error:', error);
        const { data: fallback } = await supabase
          .from('user_subscriptions')
          .select('plan_type, status, current_period_end, stripe_customer_id')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .maybeSingle();
        return fallback;
      }
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000,
    refetchInterval: 60_000,
  });

  // Admins are always treated as pro
  const plan: PlanType = isAdmin || subscription?.plan_type === 'pro' ? 'pro' : 'free';
  const isProUser = plan === 'pro';

  // Unknown (null/undefined) is optimistic — see SubscriptionData.payments_live.
  const paymentsLive = subscription?.payments_live !== false;

  const { data: usageData, isLoading: isLoadingUsage, refetch: refetchUsage } = useQuery({
    queryKey: ['usage-tracking', user?.id],
    queryFn: async () => {
      if (!user?.id) return {};
      const today = new Date().toISOString().split('T')[0];
      
      // Get Monday of current week
      const now = new Date();
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(now);
      monday.setDate(diff);
      const weekStart = monday.toISOString().split('T')[0];

      const { data } = await supabase
        .from('usage_tracking')
        .select('feature, usage_count, period_start')
        .eq('user_id', user.id)
        .gte('period_start', weekStart);

      const usageMap: Record<string, number> = {};
      if (data) {
        for (const row of data) {
          const config = FEATURE_LIMITS[row.feature as FeatureKey];
          if (!config) continue;
          
          if (config.period === 'daily' && row.period_start === today) {
            usageMap[row.feature] = row.usage_count;
          } else if (config.period === 'weekly') {
            usageMap[row.feature] = (usageMap[row.feature] || 0) + row.usage_count;
          }
        }
      }
      return usageMap;
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds
  });

  function getUsage(feature: FeatureKey) {
    const config = FEATURE_LIMITS[feature];
    const limit = config[plan];
    const current = usageData?.[feature] ?? 0;
    return { current, limit, remaining: Math.max(0, limit - current) };
  }

  function canUse(feature: FeatureKey): boolean {
    const { remaining, limit } = getUsage(feature);
    if (limit === 0) return false;
    return remaining > 0;
  }

  async function startCheckout(returnUrl?: string) {
    if (!paymentsLive && !isPreviewHost()) {
      // Test-mode key on a production host: a real card would decline with
      // "your request was in test mode". Refuse before opening checkout.
      throw new Error(paymentDisabledMessage());
    }
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { returnUrl },
      });
      if (error) {
        console.error('Checkout error:', error);
        throw new Error(error.message || 'Failed to create checkout session');
      }
      if (data?.error) {
        console.error('Checkout API error:', data.error);
        throw new Error(data.error);
      }
      if (data?.url) {
        if (data?.livemode === false) {
          console.warn('[checkout] Stripe session created in TEST mode — real cards will decline.');
        }
        openCheckoutUrl(data.url);
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout failed:', err);
      throw err;
    }
  }

  async function openCustomerPortal(returnUrl?: string) {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: { returnUrl },
      });
      if (error) {
        console.error('Portal error:', error);
        throw new Error(error.message || 'Failed to open customer portal');
      }
      if (data?.error) {
        console.error('Portal API error:', data.error);
        throw new Error(data.error);
      }
      if (data?.url) {
        openCheckoutUrl(data.url);
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (err) {
      console.error('Customer portal failed:', err);
      throw err;
    }
  }

  return {
    plan,
    isProUser,
    isLoading: isLoadingAdmin || isLoadingSubscription || isLoadingUsage,
    subscription,
    /** False when Stripe runs on a test key — paid CTAs must be inert. */
    paymentsLive,
    getUsage,
    canUse,
    refetchUsage,
    startCheckout,
    openCustomerPortal,
  };
}
